/**
 * Maps module/group identifiers from the navigation API to client-side
 * route paths. The API provides structure, ordering, and access control;
 * this registry provides the route mapping.
 *
 * When adding a new module in the backend, add a corresponding entry
 * here or it will be silently skipped in the sidebar.
 */

import type { OrganizationType } from '@/types/session';

// ============================================
// Identifier → Route Path (per org type)
// ============================================

const WORKPAY_ROUTES: Record<string, string> = {
  MODULE_HOME: '/w/home',
  // GROUP identifiers — both old seed names and live-DB renamed names supported
  CATEGORY_CONFIGURATION: '/w/settings',
  CATEGORY_SETTINGS: '/w/settings',
  CATEGORY_EMPLOYEE_DOCUMENTS: '/w/documents',
  CATEGORY_DOCUMENTS: '/w/documents',
  CATEGORY_CONTRACTS: '/w/contracts',
  MODULE_EMPLOYMENT_TYPES: '/w/employment-types',
  MODULE_FORMS: '/w/form',
  MODULE_TENANT_TEMPLATES: '/w/tenant-templates',
  MODULE_ENTITY_TEMPLATES: '/w/entity-templates',
  MODULE_EMPLOYEE_TEMPLATES: '/w/employee-templates',
  MODULE_PRODUCT_TYPES: '/w/product-types',
  MODULE_MODULE_REGISTRY: '/w/modules',
  MODULE_ROLE_MANAGEMENT: '/w/roles',
  MODULE_USER_MANAGEMENT: '/w/users',
  MODULE_TENANT_MANAGEMENT: '/w/clients',
  MODULE_ENTITY_MANAGEMENT: '/w/entities',
  MODULE_EMPLOYEES: '/w/employees',
  MODULE_CONTRACT_TEMPLATES: '/w/document-templates',
  MODULE_SIGNING_REQUESTS: '/w/signing-requests',
  MODULE_DOCUMENT_TYPES: '/w/document-types',
  MODULE_DOCUMENT_SNAPSHOTS: '/w/contracts',
  MODULE_ALL_DOCUMENTS: '/w/documents',
  MODULE_BILLING_ENGINE: '/w/billing',
};

const TENANT_ROUTES: Record<string, string> = {
  MODULE_HOME: '/t/home',
  // GROUP identifiers — both old seed names and live-DB renamed names supported
  CATEGORY_CONFIGURATION: '/t/settings',
  CATEGORY_SETTINGS: '/t/settings',
  CATEGORY_EMPLOYEE_DOCUMENTS: '/t/documents',
  CATEGORY_DOCUMENTS: '/t/documents',
  CATEGORY_CONTRACTS: '/t/contracts',
  MODULE_ROLE_MANAGEMENT: '/t/roles',
  MODULE_USER_MANAGEMENT: '/t/users',
  MODULE_EMPLOYEES: '/t/employees',
  MODULE_TENANT_PROFILE: '/t/profile/self',
  MODULE_DOCUMENT_SNAPSHOTS: '/t/contracts',
  MODULE_ALL_DOCUMENTS: '/t/documents',
  MODULE_SIGNING_REQUESTS: '/t/signing-requests',
  SUBCAT_ORG_STRUCTURE: '/t/departments',
  MODULE_DEPARTMENTS: '/t/departments',
  MODULE_JOB_TITLES: '/t/job-titles',
  MODULE_BILLING_ENGINE: '/t/billing',
};

export function getRouteForModule(
  identifier: string,
  orgType: OrganizationType,
): string | undefined {
  const routes = orgType === 'WORKPAY' ? WORKPAY_ROUTES : TENANT_ROUTES;
  return routes[identifier];
}

// ============================================
// Group membership — drives sidebar active-state for child module routes
// ============================================
//
// Sidebar items render flat (one link per ITEM/GROUP). When the user is on a
// child module page (e.g. /w/employment-types) the URL doesn't share a prefix
// with the parent group's landing route (/w/settings), so a naive prefix match
// fails to highlight the parent. This map provides the lookup.
//
// Keep in sync with the backend module_config seed under the matching
// CATEGORY_* group.
const SETTINGS_CHILDREN: readonly string[] = [
  'MODULE_ROLE_MANAGEMENT',
  'MODULE_USER_MANAGEMENT',
  'MODULE_FORMS',
  'MODULE_TENANT_TEMPLATES',
  'MODULE_ENTITY_TEMPLATES',
  'MODULE_EMPLOYEE_TEMPLATES',
  'MODULE_EMPLOYMENT_TYPES',
  'MODULE_PRODUCT_TYPES',
  'MODULE_MODULE_REGISTRY',
  'MODULE_DOCUMENT_TYPES',
  'MODULE_DEPARTMENTS',
  'MODULE_JOB_TITLES',
  'MODULE_BILLING_ENGINE',
];

const DOCUMENTS_CHILDREN: readonly string[] = ['MODULE_ALL_DOCUMENTS', 'MODULE_SIGNING_REQUESTS'];

const CONTRACTS_CHILDREN: readonly string[] = [
  'MODULE_DOCUMENT_SNAPSHOTS',
  'MODULE_CONTRACT_TEMPLATES',
];

const ORG_STRUCTURE_CHILDREN: readonly string[] = ['MODULE_DEPARTMENTS', 'MODULE_JOB_TITLES'];

const GROUP_CHILD_IDENTIFIERS: Record<string, readonly string[]> = {
  // Old seed names
  CATEGORY_CONFIGURATION: SETTINGS_CHILDREN,
  CATEGORY_EMPLOYEE_DOCUMENTS: DOCUMENTS_CHILDREN,
  CATEGORY_CONTRACTS: CONTRACTS_CHILDREN,
  // Live-DB renamed names — same children
  CATEGORY_SETTINGS: SETTINGS_CHILDREN,
  CATEGORY_DOCUMENTS: DOCUMENTS_CHILDREN,
  // Nested sub-groups within settings
  SUBCAT_ORG_STRUCTURE: ORG_STRUCTURE_CHILDREN,
};

export function getGroupChildRoutes(groupIdentifier: string, orgType: OrganizationType): string[] {
  const ids = GROUP_CHILD_IDENTIFIERS[groupIdentifier] ?? [];
  return ids.map((id) => getRouteForModule(id, orgType)).filter((r): r is string => !!r);
}

// ============================================
// Group label metadata — drives the "Back to <group>" link in AppShell
// ============================================
//
// One entry per parent group whose children should expose a back-link. Each
// group declares its own i18n key + English fallback so per-locale phrasing
// stays natural ("Volver a Configuración" vs "Volver a Contratos").
const GROUP_BACK_LABELS: Record<string, { labelKey: string; labelFallback: string }> = {
  CATEGORY_CONFIGURATION: { labelKey: 'K_BACK_TO_SETTINGS', labelFallback: 'Back to Settings' },
  CATEGORY_SETTINGS: { labelKey: 'K_BACK_TO_SETTINGS', labelFallback: 'Back to Settings' },
  CATEGORY_CONTRACTS: { labelKey: 'K_BACK_TO_CONTRACTS', labelFallback: 'Back to Contracts' },
  CATEGORY_EMPLOYEE_DOCUMENTS: {
    labelKey: 'K_BACK_TO_DOCUMENTS',
    labelFallback: 'Back to Documents',
  },
  CATEGORY_DOCUMENTS: { labelKey: 'K_BACK_TO_DOCUMENTS', labelFallback: 'Back to Documents' },
};

export interface ParentGroupLink {
  groupId: string;
  href: string;
  labelKey: string;
  labelFallback: string;
}

/**
 * For the given pathname, return the parent navigation group it belongs to
 * (if any) along with the route + label needed to render a back-link.
 *
 * Iterates `GROUP_CHILD_IDENTIFIERS`; the first group containing a child
 * route that prefix-matches `pathname` wins. Returns `null` when the path
 * is itself a group landing (e.g. `/w/settings`) or unrelated to any group.
 */
export function getParentGroupLink(
  pathname: string,
  orgType: OrganizationType,
): ParentGroupLink | null {
  for (const groupId of Object.keys(GROUP_CHILD_IDENTIFIERS)) {
    const groupRoute = getRouteForModule(groupId, orgType);
    // Skip when the user is already on the group's own landing — no back-link
    // to ourselves.
    if (groupRoute && pathname === groupRoute) continue;
    const childRoutes = getGroupChildRoutes(groupId, orgType);
    if (!childRoutes.some((r) => pathname.startsWith(r))) continue;
    if (!groupRoute) continue;
    const meta = GROUP_BACK_LABELS[groupId];
    if (!meta) continue;
    return { groupId, href: groupRoute, ...meta };
  }
  return null;
}

// ============================================
// Icon identifier → icon key
// ============================================

/**
 * Maps icon string from the API to an icon key used by the sidebar.
 * The actual SVG components are resolved in AppShell.
 */
export const ICON_MAP: Record<string, string> = {
  settings: 'configuration',
  people: 'people',
  briefcase: 'entities',
  building: 'org-profile',
  home: 'home',
};
